import { createSelector } from 'reselect';
import isUndefined from 'lodash/isUndefined';
import {
  data as dataSelector,
  data2 as data2Selector,
} from '../../AdvWorkflow/AdvWorkflowSelector';
import {
  BACKEND_KEY,
  DOLLAR_CONTRIBUTION,
  PERCENTAGE_CONTRIBUTION,
  NO_CONTRIBUTION,
  CONTRIBUTION_TYPE,
} from '../FinancialAccountsWorkflowConstants';
import { isNumeric, isEmpty } from '../../../../js/app/react/validators';

const MORTGAGE_ACCOUNT_TYPE = 13;
const CREDIT_CARD_ACCOUNT_TYPE = 8;
const SEP_IRA = 17;
const EMPLOYER_PLAN = 27;
const STUDENT_LOAN_ACCOUNT_TYPE = 16;

const LIABILITIES = 'loansCreditCards';

const DEFAULT_PERCENTAGE_CONTRIBUTION_ACCOUNTS_ID = [17, 19, 4, 22, 10, 11, 23];

const doesAccountTypeSupportTrustOwner = (selectedAccountType, selectedInsuranceType) => {
  const SEPIRA = 17;
  const SIMPLEIRA = 19;
  const EmployerPlan = 27;
  const T401K = 4;
  const Roth401K = 22;
  const T401A = 23;
  const T403B = 10;
  const T457 = 11;
  const TaxFree = 28;
  const INSURANCE = 20;

  const accountsWithNoTrust = [
    SEPIRA,
    SIMPLEIRA,
    EmployerPlan,
    T401K,
    Roth401K,
    T401A,
    T403B,
    T457,
    TaxFree,
  ];

  if (selectedAccountType === INSURANCE) {
    const LIFE_INSURANCE = 0;

    return selectedInsuranceType === LIFE_INSURANCE;
  }

  return accountsWithNoTrust.indexOf(selectedAccountType) < 0;
};

const doesAccountTypeSupportJointOwner = currentAccountTypeSelected => {
  const checkingAccountType = 0;
  const CDAccountType = 2;
  const savingsAccountType = 1;
  const brokerageAccountType = 3;
  const creditCardAccountType = 8;
  const installementAccountType = 14;
  const lineOfCreditAccountType = 15;
  const autoLoanType = 29;

  return (
    [
      checkingAccountType,
      CDAccountType,
      savingsAccountType,
      brokerageAccountType,
      creditCardAccountType,
      installementAccountType,
      lineOfCreditAccountType,
      autoLoanType,
    ].indexOf(currentAccountTypeSelected) >= 0
  );
};

// Utils

const isEmployerSponsored = account =>
  account && account.extra && account.extra.IsEmployerSponsoredRetirementAccount;

const isEmployerMatchable = account =>
  account && account.extra && account.extra.IsEmployerMatchableRetirementAccount;

const isEmployerPlan = accountDraft => accountDraft && accountDraft.AccountType === EMPLOYER_PLAN;
const isSepIra = accountDraft => accountDraft && accountDraft.AccountType === SEP_IRA;
// --

export const accountDraftSelector = createSelector(
  data2Selector,
  data2 => data2(`${BACKEND_KEY}.accountDraft`) || {},
);

export const accountTypesSelector = createSelector(
  dataSelector,
  data => data('AccountTypes') || [],
);

export const mortgageTypesOptionsSelector = createSelector(
  dataSelector,
  data => data('MortgageTypeOptions') || [],
);

export const accountOwnersSelector = createSelector(
  dataSelector,
  data => data('AccountOwners') || [],
);

export const accountTypesOptionsSelector = createSelector(accountTypesSelector, accountTypes =>
  accountTypes.map(accountType => ({
    groupId: accountType.GroupId,
    label: accountType.Label,
    value: accountType.AccountType,
    extra: accountType,
  })),
);

export const accountOwnersOptionsSelector = createSelector(
  accountOwnersSelector,
  accountDraftSelector,
  (accountOwners, accountDraft) =>
    Object.keys(accountOwners).reduce(
      (accumulator, groupKey) =>
        accumulator
          .concat(
            accountOwners[groupKey].map(accountOwner => ({
              label: accountOwner,
              value: accountOwner,
              groupId: groupKey.toLowerCase(),
            })),
          )
          .filter(({ value, groupId }) => {
            if (value === 'Joint') {
              return doesAccountTypeSupportJointOwner(accountDraft.AccountType);
            }

            if (groupId === 'trusts') {
              return doesAccountTypeSupportTrustOwner(accountDraft.AccountType);
            }

            return true;
          }),
      [],
    ),
);

export const accountTypesGroupsSelector = createSelector(
  dataSelector,
  data => data('AccountTypesGroups') || [],
);

export const nullableAccountTypeOptionSelector = createSelector(
  accountTypesOptionsSelector,
  accountDraftSelector,
  (accountTypesOptions, accountDraft) =>
    accountTypesOptions.find(option => option.value === accountDraft.AccountType) || null,
);

export const nullableAccountOwnerOptionSelector = createSelector(
  accountOwnersOptionsSelector,
  accountDraftSelector,
  (accountOwnersOptions, accountDraft) =>
    accountOwnersOptions.find(option => option.value === accountDraft.AccountOwner) || null,
);

export const monthlyContributionAmountLabelSelector = createSelector(
  accountDraftSelector,
  accountTypesSelector,
  (accountDraft, accountTypes) => {
    const { AccountType: selectedAccountType } = accountDraft;
    const groupID = accountTypes.reduce((accumulator, { AccountType, GroupId }) => {
      if (AccountType === selectedAccountType) {
        return GroupId;
      }
      return accumulator;
    }, -1);

    if (groupID === LIABILITIES) {
      return 'Monthly Payment';
    }

    const CHECKING = 0;
    const CD = 2;
    const BROKERAGE = 3;

    if (
      selectedAccountType === CHECKING ||
      selectedAccountType === CD ||
      selectedAccountType === BROKERAGE
    ) {
      return 'Deposits';
    }

    if (isEmployerPlan(accountDraft) || isSepIra(accountDraft)) {
      return 'Contributions';
    }

    return 'Contributions';
  },
);

export const nullableMortgageTypeOptionSelector = createSelector(
  mortgageTypesOptionsSelector,
  accountDraftSelector,
  (mortgageTypeOptions, accountDraft) =>
    mortgageTypeOptions.find(option => option.value === accountDraft.MortgageType) || null,
);

export const accountOwnersGroupsSelector = createSelector(accountOwnersSelector, accountOwners =>
  Object.keys(accountOwners).map(groupKey => ({
    groupId: groupKey.toLowerCase(),
    title: groupKey,
  })),
);

export const groupsSelector = createSelector(accountTypesGroupsSelector, accountTypesGroups =>
  Object.keys(accountTypesGroups).map(key => ({
    groupId: key,
    title: accountTypesGroups[key],
  })),
);

export const nullableAccountTypeSelector = createSelector(
  nullableAccountTypeOptionSelector,
  nullableAccountTypeOption =>
    !isEmpty(nullableAccountTypeOption) && isNumeric(nullableAccountTypeOption.value)
      ? +nullableAccountTypeOption.value
      : null,
);

export const interestRateLabelSelector = createSelector(
  nullableAccountTypeSelector,
  nullableAccountType => {
    switch (nullableAccountType) {
      case CREDIT_CARD_ACCOUNT_TYPE:
        return 'APR';
      default:
        return 'Interest Rate';
    }
  },
);

export const isMortgageAccountSelector = createSelector(
  nullableAccountTypeSelector,
  nullableAccountType => {
    switch (nullableAccountType) {
      case MORTGAGE_ACCOUNT_TYPE:
        return true;
      default:
        return false;
    }
  },
);

export const isStudentLoanAccountSelector = createSelector(
  nullableAccountTypeSelector,
  nullableAccountType => {
    switch (nullableAccountType) {
      case STUDENT_LOAN_ACCOUNT_TYPE:
        return true;
      default:
        return false;
    }
  },
);

export const contributionTypeSelector = createSelector(
  nullableAccountTypeOptionSelector,
  accountDraftSelector,
  (nullableAccountTypeOption, accountDraft) => {
    const annuity = 12;

    if (nullableAccountTypeOption === null || nullableAccountTypeOption.value === annuity) {
      return NO_CONTRIBUTION;
    }

    const hasDefaultPercentage =
      DEFAULT_PERCENTAGE_CONTRIBUTION_ACCOUNTS_ID.indexOf(nullableAccountTypeOption.value) >= 0;

    if (
      hasDefaultPercentage &&
      !accountDraft.ContributionPercent &&
      !accountDraft.ContributionAmount
    ) {
      return PERCENTAGE_CONTRIBUTION;
    }

    const {
      extra: { HasContributionPercentOption },
    } = nullableAccountTypeOption;

    if (accountDraft.GroupId === LIABILITIES || accountDraft.ContributionAmount) {
      return DOLLAR_CONTRIBUTION;
    }

    if (accountDraft.ContributionPercent && HasContributionPercentOption) {
      return PERCENTAGE_CONTRIBUTION;
    }

    return DOLLAR_CONTRIBUTION;
  },
);

export const contributionSelector = createSelector(
  accountDraftSelector,
  contributionTypeSelector,
  (accountDraft, contributionType) => {
    if (!accountDraft) {
      return 0;
    }

    if (accountDraft.GroupId === LIABILITIES) {
      return accountDraft.MonthlyContribution;
    }

    if (isEmployerPlan(accountDraft)) {
      return contributionType === DOLLAR_CONTRIBUTION
        ? accountDraft.EmployerMatchAmount
        : accountDraft.EmployerMatchPercent;
    }

    return contributionType === DOLLAR_CONTRIBUTION
      ? accountDraft.ContributionAmount
      : accountDraft.ContributionPercent;
  },
);

export const dollarContributionKeyNameSelector = createSelector(
  nullableAccountTypeOptionSelector,
  accountDraftSelector,
  (nullableAccountTypeOption, accountDraft) => {
    if (isEmployerPlan(accountDraft)) {
      return 'EmployerMatchAmount';
    }

    if (accountDraft.GroupId === LIABILITIES) {
      return 'MonthlyContribution';
    }

    return 'ContributionAmount';
  },
);

export const percentageContributionKeyNameSelector = createSelector(
  nullableAccountTypeOptionSelector,
  accountDraftSelector,
  (nullableAccountTypeOption, accountDraft) => {
    if (isEmployerPlan(accountDraft)) {
      return 'EmployerMatchPercent';
    }

    return 'ContributionPercent';
  },
);

export const isEmployerSponsoredRetirementAccountSelector = createSelector(
  nullableAccountTypeOptionSelector,
  nullableAccountTypeOption => isEmployerSponsored(nullableAccountTypeOption),
);

export const isEmployerMatchableRetirementAccountSelector = createSelector(
  nullableAccountTypeOptionSelector,
  nullableAccountTypeOption => isEmployerMatchable(nullableAccountTypeOption),
);

export const canEmployeeMaxContributionSelector = createSelector(
  nullableAccountTypeOptionSelector,
  accountTypeOption => {
    if (!accountTypeOption) {
      return false;
    }
    return accountTypeOption.extra.CanEmployeeMaxContribution;
  },
);

export const hasMaximizeContributionsSelector = createSelector(accountDraftSelector, account => {
  return account.MaximizeContributions;
});

export const contributionPeriodSelector = createSelector(accountDraftSelector, account => {
  const hasDefaultPercentage =
    DEFAULT_PERCENTAGE_CONTRIBUTION_ACCOUNTS_ID.indexOf(account.AccountType) >= 0;

  if (isEmployerPlan(account) || isSepIra(account) || hasDefaultPercentage) {
    return isUndefined(account.ContributionPeriod)
      ? CONTRIBUTION_TYPE.ANNUALLY
      : account.ContributionPeriod;
  }
  return isUndefined(account.ContributionPeriod)
    ? CONTRIBUTION_TYPE.MONTHLY
    : account.ContributionPeriod;
});

export const hasContributionPeriodOptionSelector = createSelector(
  nullableAccountTypeOptionSelector,
  accountTypeOption => {
    if (!accountTypeOption) {
      return false;
    }
    return accountTypeOption.extra.HasContributionPeriodOption;
  },
);

export const hasContributionPercentOptionSelector = createSelector(
  nullableAccountTypeOptionSelector,
  accountTypeOption => {
    if (!accountTypeOption) {
      return false;
    }
    return accountTypeOption.extra.HasContributionPercentOption;
  },
);
